import type { DeviceCategory } from '@mvcp/contracts';

/**
 * The category list, in contract order, for every category select.
 *
 * Hand-copied from `DEVICE_CATEGORIES` because the web consumes `@mvcp/contracts` as TYPES
 * ONLY — importing the contract's runtime array breaks the production `vite build` (CJS
 * `__exportStar` is unresolvable by rollup; Story 1.6). Same workaround as
 * `FLAG_STATE_OPTIONS` / `ROLE_OPTIONS` / `SCOPE_OPTIONS`, and guarded the same way:
 * `lib/enum-sync.test.ts` runs under vitest rather than rollup, so it CAN import the
 * canonical array and asserts this copy stays byte-equal to it.
 *
 * ORDER IS PART OF THE CONTRACT here, not just the values — this array drives the option
 * order in every category select, and `DEVICE_CATEGORIES` deliberately puts the `Other`
 * fallback last. A `Record<DeviceCategory, …>` gives completeness; only this gives order.
 *
 * Lives in `lib/` rather than beside the icon component because `import-parse.ts` needs the
 * normalizer below, and a `lib/` module importing from `shells/` would invert the layering.
 */
export const DEVICE_CATEGORY_OPTIONS = [
  'Navigation',
  'Communication',
  'Automation',
  'Propulsion',
  'Safety',
  'Sonar',
  'Other',
] as const satisfies readonly DeviceCategory[];

/**
 * A free-text cell → the category it means.
 *
 * The importer is the one path where a category still arrives as arbitrary text: a
 * spreadsheet column is whatever the Manager typed. Since `039_device_category_enum.sql`
 * closed the column, sending that text through unchanged would earn a 400 from the commit
 * endpoint that the Manager could not have predicted from the preview.
 *
 * So the value is resolved HERE, at parse time, rather than at the commit boundary. That is
 * deliberate: the wizard shows the parsed category in its preview, and normalizing later
 * would make the preview show one value and the commit send another — a divergence the
 * Manager has no way to see. Normalizing at parse means what you review is what you commit.
 *
 * Case- and whitespace-insensitive, matching `039`'s `lower(btrim(...))` fold, so `'safety '`
 * lands on `Safety` rather than being swept aside by a difference anyone would call a typo.
 *
 * Blank/absent → `undefined` (absence, exactly as AD-24 reads a blank cell). An unrecognised
 * value → `'Other'`, matching both the migration and the API's read path: the cell DOES
 * record a category, it is simply outside our list, and mapping it to `undefined` would
 * quietly re-brand a stated fact as "not recorded".
 */
export function normalizeDeviceCategory(raw: string | undefined): DeviceCategory | undefined {
  const trimmed = (raw ?? '').trim();
  if (trimmed === '') return undefined;
  const folded = trimmed.toLowerCase();
  return DEVICE_CATEGORY_OPTIONS.find((c) => c.toLowerCase() === folded) ?? 'Other';
}
